import { useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import type { DokumentyDokumentDto } from '@gov-nx/api';
import {
	createDocumentQuery,
	deleteDocumentImageQuery,
	documentDeleteQuery,
	documentQuery,
	updateDocumentImagesQuery,
	updateDocumentQuery,
	useAxiosInstance,
	usePoMutation,
	usePoQuery,
} from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import {
	DefaultUploadParameters,
	useDocumentImageDownload,
	useFileUpload,
} from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { today, useBoolean } from '@gov-nx/utils/common';
import type { PageCode } from '../../definitions/codes';
import { useMyFilesDownload } from '../../moje-soubory/seznam/hooks/useMyFilesDownload';
import { ownDocumentKeys } from '../queryKeys';
import {
	FormInstanceNative,
	FormInstanceWeb,
	prepareFiles,
	prepareSubmitData,
} from './FormDefinition';
import type {
	MyOwnDocumentsContext,
	OwnDocumentFormData,
} from './context.types';
import { OwnDokumentPreviewExtensions } from './context.types';

const VlastniDokladContext =
	createContext<Nullable<MyOwnDocumentsContext>>(null);

interface VlastniDokladyContextProviderProps {
	children: ReactNode;
	code: PageCode;
	documentId?: number;
	onDocumentUpdated: () => void;
	isWeb?: boolean;
}

export const VlastniDokladyContextProvider = ({
	code,
	children,
	documentId,
	onDocumentUpdated,
	isWeb = false,
}: VlastniDokladyContextProviderProps) => {
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { t } = useLocale(code);
	const queryClient = useQueryClient();
	const formDefinition = isWeb
		? FormInstanceWeb({ code })
		: FormInstanceNative({ code });
	const isDeletePromptOpen = useBoolean(false);
	const hasFile = useBoolean(false);
	const hasPreview = useBoolean(false);
	const documentExist = !!documentId;
	const [imageBlob, setImageBlob] = useState<Blob | null>(null);
	const myFilesDownload = useMyFilesDownload({});
	const [soubory, setSoubory] = useState<DokumentyDokumentDto['soubory']>([]);
	const [fileForPreview, setFileForPreview] =
		useState<MyOwnDocumentsContext['fileForPreview']>(null);
	const [fileToDelete, setFileToDelete] = useState<string | null>(null);
	const [downloadedFile, setDownloadedFile] = useState<string | undefined>();
	const { axiosInstance } = useAxiosInstance();

	const query = usePoQuery({
		queryKey: ownDocumentKeys().one(documentId),
		queryFn: async () =>
			documentId ? documentQuery(axiosInstance)(documentId) : undefined,
		enabled: documentExist,
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		const response = query.data;
		if (!response) return;

		hasFile.setValue((response.soubory ?? []).length > 0);

		hasPreview.setValue(
			(response.soubory ?? []).some((soubor) =>
				soubor.pripona
					? OwnDokumentPreviewExtensions.includes(soubor.pripona.toLowerCase())
					: false
			)
		);

		setSoubory(response.soubory ?? []);

		formDefinition.formMethods.reset({
			nazev: response.nazev,
			cisloDokumentu: response.cisloDokumentu,
			komentar: response.komentar,
			platnostOd: response.platnostOd ? today(response.platnostOd) : undefined,
			platnostDo: response.platnostDo ? today(response.platnostDo) : undefined,
			_replaceFile: false,
			nazevSouboru: response.soubory?.[0]?.nazev,
			soubor: undefined,
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query.data]);

	const downloadImage = useDocumentImageDownload({
		onSuccess: (blob) => {
			setImageBlob(blob);
		},
		config: {
			errorIgnoreFilter: () => true,
		},
	});

	const documentEditMutation = usePoMutation({
		mutationFn: async (formData: OwnDocumentFormData) => {
			const prepared = prepareSubmitData(formData);
			return documentId
				? updateDocumentQuery(axiosInstance)(documentId, prepared)
				: undefined;
		},
		onSuccess: async (_response, variables) => {
			if ((variables.soubor?.length ?? 0) > 0) {
				documentAppendImagesMutation.mutate(variables.soubor);
				return;
			}
			toastMessageSuccess(t('formular.zprava.ulozeno'));
			await queryClient.invalidateQueries({ queryKey: ['documents'] });
			await queryClient.invalidateQueries({ queryKey: ownDocumentKeys().all });
			onDocumentUpdated();
			await queryClient.invalidateQueries({
				queryKey: ownDocumentKeys().preview(documentId),
			});
		},
	});

	const documentAppendImagesMutation = usePoMutation({
		mutationFn: async (formData: OwnDocumentFormData['soubor']) => {
			const preparedFiles = prepareFiles(formData);
			return documentId
				? updateDocumentImagesQuery(axiosInstance)(documentId, preparedFiles)
				: undefined;
		},
		onSuccess: async () => {
			toastMessageSuccess(t('formular.zprava.ulozeno'));
			await queryClient.invalidateQueries({ queryKey: ['documents'] });
			await queryClient.invalidateQueries({ queryKey: ownDocumentKeys().all });
			onDocumentUpdated();
			await queryClient.invalidateQueries({
				queryKey: ownDocumentKeys().preview(documentId),
			});
		},
	});

	const documentDeleteImageMutation = usePoMutation<
		AxiosResponse<void>,
		{ dokumentId: number; souborId: string }
	>(
		{
			mutationFn: deleteDocumentImageQuery(axiosInstance),
			onSuccess: async () => {
				toastMessageSuccess(t('formular.zprava.smazano'));
				await queryClient.invalidateQueries({ queryKey: ['documents'] });
				await queryClient.invalidateQueries({
					queryKey: ownDocumentKeys().all,
				});
				await queryClient.invalidateQueries({
					queryKey: ownDocumentKeys().preview(documentId),
				});
			},
		},
		{ errorIgnoreFilter: () => true }
	);

	const documentCreateMutation = usePoMutation({
		mutationFn: async (formData: OwnDocumentFormData) => {
			const prepared = prepareSubmitData(formData);
			return createDocumentQuery(axiosInstance)(prepared);
		},
		onSuccess: async () => {
			toastMessageSuccess(t('formular.zprava.ulozeno'));
			await queryClient.invalidateQueries({ queryKey: ['documents'] });
			await queryClient.invalidateQueries({ queryKey: ownDocumentKeys().all });
			await queryClient.invalidateQueries({
				queryKey: ownDocumentKeys().preview(documentId),
			});

			onDocumentUpdated();
		},
	});

	const ownDocumentDeleteMutation = usePoMutation({
		mutationFn: documentDeleteQuery(axiosInstance),
		onSuccess: async () => {
			toastMessageSuccess(t('formular.zprava.smazano'));
			await queryClient.invalidateQueries({ queryKey: ['documents'] });
			await queryClient.invalidateQueries({ queryKey: ['documents-binned'] });
			await queryClient.invalidateQueries({ queryKey: ownDocumentKeys().all });
			onDocumentUpdated();
		},
	});

	const handleEditSubmit = formDefinition.formMethods.handleSubmit(() =>
		documentEditMutation.mutate(formDefinition.formMethods.getValues())
	);

	const handleCreateSubmit = formDefinition.formMethods.handleSubmit(() =>
		documentCreateMutation.mutate(formDefinition.formMethods.getValues())
	);

	const downloadFile = () => {
		documentId &&
			soubory?.[0]?.souborId &&
			myFilesDownload.downloadDocument({
				documentId,
				souborId: soubory?.[0]?.souborId,
			});
	};

	const fileUploadHook = useFileUpload(formDefinition.formMethods, 'soubor', {
		...DefaultUploadParameters,
		multiple: true,
		maxAttachments: 5 - (soubory?.length ?? 0),
		type: ['application/pdf', 'image/*'],
		extensions: ['.jpg', '.jpeg', '.png', '.pdf'],
	});

	return (
		<VlastniDokladContext.Provider
			value={{
				code,
				controls: {
					initialLoading: query.isInitialLoading,
					initialError: query.error,
					processError: documentExist
						? documentEditMutation.error || documentAppendImagesMutation.error
						: documentCreateMutation.error,
					processLoading: documentExist
						? documentEditMutation.isLoading ||
							documentAppendImagesMutation.isLoading
						: documentCreateMutation.isLoading,
				},
				hasPreview: hasPreview.value,
				isEditAction: documentExist,
				documentId,
				formDefinition,
				onOwnDocumentDelete: ownDocumentDeleteMutation.mutate,
				isDeletePromptOpen,
				replaceFile: formDefinition.formMethods.watch('_replaceFile') ?? false,
				onSubmit: documentExist ? handleEditSubmit : handleCreateSubmit,
				hasFile: hasFile.value,
				imageBlob,
				myFilesDownload,
				downloadedFile,
				setDownloadedFile,
				downloadImage: {
					mutation: downloadImage,
					fetchFullFile: async ({
						documentId,
						souborId,
						onSuccess,
					}: {
						documentId: number;
						souborId: string;
						onSuccess: (result: Blob) => void;
					}) => {
						setDownloadedFile(souborId);
						try {
							const res = await downloadImage.mutateAsync({
								documentId,
								souborId,
							});
							onSuccess(res.data);
						} catch (e) {
							toastMessageError(t('akce.chyba.stahnout'));
						}
						setDownloadedFile(undefined);
					},
				},
				onDeleteImage: async (dokumentId: number, souborId: string) => {
					try {
						return await documentDeleteImageMutation.mutateAsync({
							dokumentId,
							souborId,
						});
					} catch (e) {
						toastMessageError(t('akce.chyba.odstranit'));
					}
				},
				fileForPreview,
				setFileForPreview,
				fileToDelete,
				setFileToDelete,
				downloadFile,
				soubory,
				fileUploadHook,
			}}>
			{children}
		</VlastniDokladContext.Provider>
	);
};

export const useVlastniDokladyContext = (): MyOwnDocumentsContext =>
	useContext(VlastniDokladContext) as MyOwnDocumentsContext;
